// ScoringConfigList.tsx - List view for scoring configurations

import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { ScoringConfiguration } from '../../types/ScoringConfiguration';
import { StandardCard, StandardButton } from '../ui';

interface ScoringConfigListProps {
  configurations: ScoringConfiguration[];
  activeConfigId: string;
  onEdit: (config: ScoringConfiguration) => void;
  onDelete: (configId: string) => void;
  onSetActive: (configId: string) => void;
  onTest: (config: ScoringConfiguration) => void;
  onExport: (config: ScoringConfiguration) => void;
  onImport: (file: File) => void;
}

export const ScoringConfigList: React.FC<ScoringConfigListProps> = ({
  configurations,
  activeConfigId,
  onEdit,
  onDelete,
  onSetActive,
  onTest,
  onExport,
  onImport
}) => {
  const { isDark } = useTheme();
  const importInputRef = useRef<HTMLInputElement>(null);

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onImport(file);
      event.target.value = ''; // Reset input
    }
  };

  const getDifficultyColor = (multiplier: number) => {
    if (multiplier >= 1.3) return isDark ? 'text-red-400' : 'text-red-600';
    if (multiplier >= 1.1) return isDark ? 'text-orange-400' : 'text-orange-600';
    if (multiplier <= 0.8) return isDark ? 'text-green-400' : 'text-green-600';
    return isDark ? 'text-blue-400' : 'text-blue-600';
  };

  const getDifficultyLabel = (multiplier: number) => {
    if (multiplier >= 1.3) return 'Hard';
    if (multiplier >= 1.1) return 'Challenging';
    if (multiplier <= 0.8) return 'Easy';
    if (multiplier <= 0.9) return 'Forgiving';
    return 'Normal';
  };

  return (
    <div className="space-y-4">
      {/* Import Button */}
      <div className="flex justify-end">
        <input
          ref={importInputRef}
          type="file"
          accept=".json"
          onChange={handleFileChange}
          className="hidden"
        />
        <StandardButton
          onClick={handleImportClick}
          variant="secondary"
          size="small"
          leftIcon="📥"
        >
          Import Configuration
        </StandardButton>
      </div>

      {/* Configuration List */}
      <div className="grid gap-4">
        {configurations.map((config) => (
          <motion.div
            key={config.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            layout
          >
            <StandardCard
              variant="elevated"
              padding="large"
              hoverable
              selected={config.id === activeConfigId}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className={`text-lg font-semibold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {config.name}
                    </h3>
                    {config.id === activeConfigId && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isDark 
                          ? 'bg-green-900/30 text-green-400 border border-green-700' 
                          : 'bg-green-100 text-green-800 border border-green-200'
                      }`}>
                        Active
                      </span>
                    )}
                    {config.id === 'default' && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isDark 
                          ? 'bg-blue-900/30 text-blue-400 border border-blue-700' 
                          : 'bg-blue-100 text-blue-800 border border-blue-200'
                      }`}>
                        System Default
                      </span>
                    )}
                  </div>

                  <p className={`text-sm mb-4 ${
                    isDark ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {config.description}
                  </p>

                  {/* Configuration Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className={`text-xs font-medium mb-1 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Difficulty
                      </p>
                      <p className={`text-sm font-semibold ${
                        getDifficultyColor(config.difficultyMultiplier)
                      }`}>
                        {getDifficultyLabel(config.difficultyMultiplier)}
                      </p>
                    </div>

                    <div>
                      <p className={`text-xs font-medium mb-1 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Base Scores
                      </p>
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {Math.round(
                          (config.baseScores.security + 
                           config.baseScores.reputation + 
                           config.baseScores.financial + 
                           config.baseScores.operational) / 4
                        )} avg
                      </p>
                    </div>

                    <div>
                      <p className={`text-xs font-medium mb-1 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Synergies
                      </p>
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {config.enableSynergies ? '✓ Enabled' : '✗ Disabled'}
                      </p>
                    </div>

                    <div>
                      <p className={`text-xs font-medium mb-1 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Time Bonus
                      </p>
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {config.enableResponseTimeBonus ? '✓ Enabled' : '✗ Disabled'}
                      </p>
                    </div>
                  </div>

                  {/* Weight Distribution */}
                  <div className="flex items-center gap-4 text-xs">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                      Weights:
                    </span>
                    <div className="flex gap-3">
                      <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>
                        Security {Math.round(config.categoryWeights.security * 100)}%
                      </span>
                      <span className={isDark ? 'text-purple-400' : 'text-purple-600'}>
                        Reputation {Math.round(config.categoryWeights.reputation * 100)}%
                      </span>
                      <span className={isDark ? 'text-green-400' : 'text-green-600'}>
                        Financial {Math.round(config.categoryWeights.financial * 100)}%
                      </span>
                      <span className={isDark ? 'text-orange-400' : 'text-orange-600'}>
                        Operational {Math.round(config.categoryWeights.operational * 100)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 ml-4">
                  {config.id !== activeConfigId && (
                    <StandardButton
                      onClick={() => onSetActive(config.id)}
                      variant="primary"
                      size="small"
                      fullWidth
                    >
                      Set Active
                    </StandardButton>
                  )}
                  <StandardButton
                    onClick={() => onTest(config)}
                    variant="secondary"
                    size="small"
                    fullWidth
                  >
                    Test
                  </StandardButton>
                  <StandardButton
                    onClick={() => onEdit(config)}
                    variant="secondary"
                    size="small"
                    fullWidth
                  >
                    Edit
                  </StandardButton>
                  <StandardButton
                    onClick={() => onExport(config)}
                    variant="secondary"
                    size="small"
                    fullWidth
                  >
                    Export
                  </StandardButton>
                  {config.id !== 'default' && (
                    <StandardButton
                      onClick={() => {
                        if (window.confirm(`Delete "${config.name}"?`)) {
                          onDelete(config.id);
                        }
                      }}
                      variant="danger"
                      size="small"
                      fullWidth
                    >
                      Delete
                    </StandardButton>
                  )}
                </div>
              </div>

              {/* Last Updated */}
              <div className={`mt-4 pt-4 border-t text-xs ${
                isDark 
                  ? 'border-white/10 text-gray-400' 
                  : 'border-gray-200 text-gray-500'
              }`}>
                Last updated: {new Date(config.updatedAt).toLocaleDateString()} • Version {config.version}
              </div>
            </StandardCard>
          </motion.div>
        ))}
      </div>

      {configurations.length === 0 && (
        <StandardCard variant="outlined" padding="large">
          <div className="text-center py-8">
            <div className="text-6xl mb-4">📊</div>
            <h3 className={`text-lg font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              No Scoring Configurations
            </h3>
            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
              Create your first configuration or apply a preset to get started
            </p>
          </div>
        </StandardCard>
      )}
    </div>
  );
};